// @spec IAM-STR-034
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuditorAccessByToken } from '@/features/auditor-access/api/use-auditor-access-by-token';
import { AuditorAccessExpired } from '@/features/auditor-access/components/AuditorAccessExpired';
import { AuditorLandingPage } from '@/features/auditor-access/components/AuditorLandingPage';
import type { AuditorAccessInvalidReason } from '@/features/auditor-access/types';

export const Route = createFileRoute('/auditor/$token')({
  component: AuditorAccessPage,
});

/**
 * Maps API error codes to display reasons for the AuditorAccessExpired component.
 */
function getInvalidReason(
  errorCode: string | null,
  status: number | undefined
): AuditorAccessInvalidReason {
  if (errorCode === 'EXPIRED' || status === 410) {
    return 'expired';
  }
  if (errorCode === 'REVOKED') {
    return 'revoked';
  }
  // Default for 404 or unknown errors
  return 'not_found';
}

/**
 * Loading skeleton for the auditor access page.
 */
function AuditorAccessSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Skeleton className="h-20 w-20 rounded-full" />
          </div>
          <Skeleton className="mx-auto h-8 w-48" />
          <Skeleton className="mx-auto mt-2 h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="mx-auto h-3 w-56" />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Auditor access landing page route component.
 *
 * Fetches auditor access details by token from the URL and displays:
 * - Loading skeleton while fetching
 * - AuditorAccessExpired for invalid/expired access
 * - AuditorLandingPage for valid pending access
 */
function AuditorAccessPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, errorCode } = useAuditorAccessByToken({ token });

  const handleAccepted = () => {
    // Navigate to dashboard after accepting
    void navigate({ to: '/' });
  };

  // Show loading skeleton
  if (isLoading) {
    return <AuditorAccessSkeleton />;
  }

  // Show error state for invalid access
  if (isError || !data) {
    const reason = getInvalidReason(errorCode, error?.status);
    return <AuditorAccessExpired reason={reason} />;
  }

  // Show landing page
  return <AuditorLandingPage accessDetails={data} token={token} onAccepted={handleAccepted} />;
}
